/**
 * Stats Counter — count-up animation.
 *
 * Watches each .block-stats-counter with an IntersectionObserver and animates
 * every .block-stats-counter__value from 0 to its final number when the block
 * scrolls into view. Fires once per page load.
 */

const DURATION = 2000; // ms

/**
 * Easing: ease-out cubic — fast start, gentle finish.
 *
 * @param {number} t Progress 0–1.
 * @returns {number} Eased progress 0–1.
 */
function easeOutCubic( t ) {
	return 1 - Math.pow( 1 - t, 3 );
}

/**
 * Format a number to match the original value's decimal places.
 *
 * @param {number} value      Current animated value.
 * @param {number} decimals   Number of decimal places to preserve.
 * @param {boolean} useCommas Whether the original used comma separators.
 * @returns {string}
 */
function formatNumber( value, decimals, useCommas ) {
	const fixed = value.toFixed( decimals );
	if ( ! useCommas ) {
		return fixed;
	}
	const [ integer, decimal ] = fixed.split( '.' );
	const formatted = integer.replace( /\B(?=(\d{3})+(?!\d))/g, ',' );
	return decimal !== undefined ? `${ formatted }.${ decimal }` : formatted;
}

/**
 * Animate a single value element from 0 to its target.
 *
 * @param {HTMLElement} el      The .block-stats-counter__value element.
 * @param {number}      target  Final numeric value.
 * @param {number}      decimals Decimal places to preserve.
 * @param {boolean}     useCommas Whether to re-add comma separators.
 */
function animateValue( el, target, decimals, useCommas ) {
	const start = performance.now();

	function step( now ) {
		const elapsed  = now - start;
		const progress = Math.min( elapsed / DURATION, 1 );
		const eased    = easeOutCubic( progress );
		el.textContent = formatNumber( eased * target, decimals, useCommas );

		if ( progress < 1 ) {
			requestAnimationFrame( step );
		} else {
			el.textContent = formatNumber( target, decimals, useCommas );
		}
	}

	requestAnimationFrame( step );
}

/**
 * Initialise count-up for all stats counter blocks on the page.
 */
export function initStatsCounter() {
	const blocks = document.querySelectorAll( '.block-stats-counter' );
	if ( ! blocks.length ) {
		return;
	}

	const observer = new IntersectionObserver(
		( entries, obs ) => {
			entries.forEach( ( entry ) => {
				if ( ! entry.isIntersecting ) {
					return;
				}

				obs.unobserve( entry.target );

				entry.target.querySelectorAll( '.block-stats-counter__value' ).forEach( ( el ) => {
					const raw      = el.textContent.trim();
					const useCommas = raw.includes( ',' );
					const cleaned  = raw.replace( /,/g, '' );
					const target   = parseFloat( cleaned );

					if ( isNaN( target ) ) {
						return;
					}

					const decimals = ( cleaned.split( '.' )[ 1 ] || '' ).length;
					el.textContent = formatNumber( 0, decimals, useCommas );
					animateValue( el, target, decimals, useCommas );
				} );
			} );
		},
		{ threshold: 0.2 }
	);

	blocks.forEach( ( block ) => observer.observe( block ) );
}
